import { Modal } from '@chopchop-se/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ArticleDetail } from '@warehouse/shared';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { toastError, toastSuccess } from '../../../lib/notify';
import { createArticle } from '../api';
import { ArticleForm, toPayload, type ArticleFormValues } from './ArticleForm';

/** "Ny artikel" — the create-only wrapper around the shared article form. */
export function ArticleFormModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (article: ArticleDetail) => void;
}): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (values: ArticleFormValues) => createArticle(toPayload(values)),
    onSuccess: (article) => {
      void queryClient.invalidateQueries({ queryKey: ['master-data', 'articles'] });
      toastSuccess(t('masterData:articles.created'));
      onCreated(article);
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  return (
    <Modal open onClose={onClose} title={t('masterData:articles.new')} width={860}>
      <ArticleForm
        submitting={mutation.isPending}
        submitLabel={t('common:actions.save')}
        onCancel={onClose}
        onSubmit={(values) => {
          mutation.mutate(values);
        }}
      />
    </Modal>
  );
}
